import { Album, ShelfPosition, ShelfConfig, ShelfBoundary } from '../types/album';

// Re-export types for convenience
export type { ShelfConfig, ShelfBoundary, ShelfPosition } from '../types/album';

/**
 * Generic Shelf Position Calculation Utility
 * 
 * Handles proportional positioning based on album thickness and user-defined shelf boundaries.
 * Supports configurable grid layouts and shelf dimensions.
 */

export interface ShelfVisualizationData {
  overview: {
    rows: number;
    columns: number;
    currentShelf: number;
    shelfLayout: number[][]; // Maps grid positions to shelf numbers
    shelfDimensions: {
      heightRatio: number;
      widthRatio: number;
    };
  };
  currentShelf: {
    shelfNumber: number;
    totalAlbums: number;
    currentPosition: number;
    visualSections: number; // For rendering vertical divisions
  };
}

export const DEFAULT_SHELF_CONFIG: ShelfConfig = {
  rows: 4,
  columns: 3,
  activeShelves: 8,
  shelfDimensions: {
    heightRatio: 1.0, // Square shelves by default
    widthRatio: 1.0,
  },
  shelfBoundaries: [], // Will be populated by user in settings
};

/**
 * Calculate shelf layout for configurable grid (reading order: left-to-right, top-to-bottom)
 * Returns a 2D array mapping grid positions to shelf numbers (1-N, 0 for unused)
 */
export function generateShelfLayout(config: ShelfConfig): number[][] {
  const layout: number[][] = [];
  let shelfNumber = 1;
  
  for (let row = 0; row < config.rows; row++) {
    const rowArray: number[] = [];
    for (let col = 0; col < config.columns; col++) {
      if (shelfNumber <= config.activeShelves) {
        rowArray.push(shelfNumber);
        shelfNumber++;
      } else {
        rowArray.push(0); // Unused shelf
      }
    }
    layout.push(rowArray);
  }
  
  return layout;
}

/**
 * Find which shelf an album belongs to based on alphabetical sorting and shelf boundaries
 */
export function findAlbumShelf(
  album: Album,
  allAlbums: Album[],
  config: ShelfConfig
): number {
  if (config.shelfBoundaries.length === 0) {
    // Fallback: distribute evenly if no boundaries set
    return distributeEvenly(album, allAlbums, config.activeShelves);
  }
  
  // Sort albums alphabetically
  const sortedAlbums = [...allAlbums].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  const albumIndex = sortedAlbums.findIndex(a => a.id === album.id);
  
  if (albumIndex === -1) return 0;
  
  // Find which shelf boundary this album falls into
  for (const boundary of config.shelfBoundaries) {
    const firstIndex = sortedAlbums.findIndex(a => a.id === boundary.firstAlbumId);
    const lastIndex = sortedAlbums.findIndex(a => a.id === boundary.lastAlbumId);
    
    if (firstIndex !== -1 && lastIndex !== -1 && albumIndex >= firstIndex && albumIndex <= lastIndex) {
      return boundary.shelfNumber;
    }
  }
  
  return 0; // Not found in any defined boundary
}

/**
 * Calculate position within a specific shelf using physical size spacing
 */
export function calculatePositionInShelf(
  album: Album,
  allAlbums: Album[],
  shelfNumber: number,
  config: ShelfConfig
): { position: number; total: number } {
  const boundary = config.shelfBoundaries.find(b => b.shelfNumber === shelfNumber);
  if (!boundary) return { position: 0, total: 0 };
  
  const sortedAlbums = [...allAlbums].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  const firstIndex = sortedAlbums.findIndex(a => a.id === boundary.firstAlbumId);
  const lastIndex = sortedAlbums.findIndex(a => a.id === boundary.lastAlbumId);
  const albumIndex = sortedAlbums.findIndex(a => a.id === album.id);
  
  if (firstIndex === -1 || lastIndex === -1 || albumIndex === -1) {
    return { position: 0, total: 0 };
  }
  
  // Get albums in this shelf
  const albumsInShelf = sortedAlbums.slice(firstIndex, lastIndex + 1);
  
  // Calculate cumulative physical size up to current album
  let cumulativeSize = 0;
  let totalShelfSize = 0;
  let currentAlbumPosition = 0;
  
  for (let i = 0; i < albumsInShelf.length; i++) {
    const currentAlbum = albumsInShelf[i];
    const physicalSize = currentAlbum.format?.physicalSize || 1.0; // Default to standard LP thickness
    
    if (currentAlbum.id === album.id) {
      currentAlbumPosition = cumulativeSize + (physicalSize / 2); // Center of album
    }
    
    cumulativeSize += physicalSize;
    totalShelfSize += physicalSize;
  }
  
  // Calculate position as a ratio of actual shelf size
  const normalizedPosition = Math.round((currentAlbumPosition / totalShelfSize) * albumsInShelf.length);
  
  // For shelves with boundaries, use actual album count between first and last
  const shelfBoundary = config.shelfBoundaries.find(b => b.shelfNumber === shelfNumber);
  let totalForShelf = albumsInShelf.length;
  
  if (shelfNumber === config.activeShelves) {
    // For the last shelf, ALWAYS use average capacity of other shelves
    // This prevents the few leftover albums from spreading across the entire visual width
    const otherShelves = config.shelfBoundaries.filter(b => b.shelfNumber !== shelfNumber);
    if (otherShelves.length > 0) {
      const averageCapacity = otherShelves.reduce((sum, shelf) => sum + shelf.albumCount, 0) / otherShelves.length;
      totalForShelf = Math.round(averageCapacity);
    }
  } else if (shelfBoundary) {
    // Use the actual album count defined by the boundary configuration
    totalForShelf = shelfBoundary.albumCount;
  }
  
  return { 
    position: Math.max(1, normalizedPosition), 
    total: totalForShelf 
  };
}

/**
 * Calculate shelf positions for all albums
 */
export function calculateShelfPositions(
  albums: Album[],
  config: ShelfConfig
): Album[] {
  return albums.map(album => {
    const shelfNumber = findAlbumShelf(album, albums, config);
    const { position, total } = calculatePositionInShelf(album, albums, shelfNumber, config);
    
    const shelfPosition: ShelfPosition = {
      section: shelfNumber,
      estimatedPosition: position,
      totalInSection: total,
    };
    
    return {
      ...album,
      shelfPosition,
    };
  });
}

/**
 * Generate visualization data for the shelf display
 */
export function generateVisualizationData(
  album: Album,
  allAlbums: Album[],
  config: ShelfConfig
): ShelfVisualizationData {
  const layout = generateShelfLayout(config);
  const shelfNumber = findAlbumShelf(album, allAlbums, config);
  const { position, total } = calculatePositionInShelf(album, allAlbums, shelfNumber, config);
  
  return {
    overview: {
      rows: config.rows,
      columns: config.columns,
      currentShelf: shelfNumber,
      shelfLayout: layout,
      shelfDimensions: config.shelfDimensions,
    },
    currentShelf: {
      shelfNumber,
      totalAlbums: total,
      currentPosition: position,
      visualSections: Math.max(10, Math.min(total, 50)), // Reasonable number of visual divisions
    },
  };
}

/**
 * Calculate shelf boundaries automatically from user input
 */
export function calculateShelfBoundaries(
  shelfDefinitions: Array<{
    shelfNumber: number;
    firstAlbumId: string;
    lastAlbumId: string;
  }>,
  allAlbums: Album[]
): ShelfBoundary[] {
  const sortedAlbums = [...allAlbums].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  
  return shelfDefinitions.map(def => {
    const firstIndex = sortedAlbums.findIndex(a => a.id === def.firstAlbumId);
    const lastIndex = sortedAlbums.findIndex(a => a.id === def.lastAlbumId);
    const albumCount = firstIndex !== -1 && lastIndex !== -1 ? lastIndex - firstIndex + 1 : 0;
    
    return {
      shelfNumber: def.shelfNumber,
      firstAlbumId: def.firstAlbumId,
      lastAlbumId: def.lastAlbumId,
      albumCount,
    };
  }).sort((a, b) => a.shelfNumber - b.shelfNumber);
}

/**
 * Fallback: even distribution when no boundaries are set
 */
function distributeEvenly(album: Album, allAlbums: Album[], totalShelves: number): number {
  const sortedAlbums = [...allAlbums].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  const albumIndex = sortedAlbums.findIndex(a => a.id === album.id);
  
  if (albumIndex === -1) return 0;
  
  const albumsPerShelf = Math.ceil(sortedAlbums.length / totalShelves);
  return Math.min(Math.floor(albumIndex / albumsPerShelf) + 1, totalShelves);
}

/**
 * Get albums that should be in a specific shelf
 */
export function getAlbumsInShelf(
  shelfNumber: number,
  allAlbums: Album[],
  config: ShelfConfig
): Album[] {
  const boundary = config.shelfBoundaries.find(b => b.shelfNumber === shelfNumber);
  
  if (boundary) {
    // Use configured boundary
    const sortedAlbums = [...allAlbums].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    const firstIndex = sortedAlbums.findIndex(a => a.id === boundary.firstAlbumId);
    const lastIndex = sortedAlbums.findIndex(a => a.id === boundary.lastAlbumId);
    
    if (firstIndex !== -1 && lastIndex !== -1) {
      return sortedAlbums.slice(firstIndex, lastIndex + 1);
    }
  }
  
  // Fallback: use even distribution for shelves without boundaries
  const sortedAlbums = [...allAlbums].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  const albumsPerShelf = Math.ceil(sortedAlbums.length / config.activeShelves);
  const shelfStart = (shelfNumber - 1) * albumsPerShelf;
  const shelfEnd = Math.min(shelfStart + albumsPerShelf, sortedAlbums.length);
  
  return sortedAlbums.slice(shelfStart, shelfEnd);
}

/**
 * Validate shelf configuration
 */
export function validateShelfConfiguration(
  config: ShelfConfig,
  allAlbums: Album[]
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  const sortedAlbums = [...allAlbums].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  
  // Validate grid dimensions
  if (config.rows <= 0 || config.columns <= 0) {
    errors.push('Grid dimensions must be positive numbers');
  }
  
  if (config.activeShelves > config.rows * config.columns) {
    errors.push(`Cannot have ${config.activeShelves} active shelves in a ${config.rows}x${config.columns} grid`);
  }
  
  // Validate shelf dimensions
  if (config.shelfDimensions.heightRatio <= 0 || config.shelfDimensions.widthRatio <= 0) {
    errors.push('Shelf dimensions must be positive numbers');
  }
  
  // Check if all boundaries are defined for active shelves
  if (config.shelfBoundaries.length !== config.activeShelves) {
    errors.push(`Expected ${config.activeShelves} shelf boundaries, but got ${config.shelfBoundaries.length}`);
  }
  
  // Check for overlapping boundaries
  for (let i = 0; i < config.shelfBoundaries.length - 1; i++) {
    const current = config.shelfBoundaries[i];
    const next = config.shelfBoundaries[i + 1];
    
    const currentLastIndex = sortedAlbums.findIndex(a => a.id === current.lastAlbumId);
    const nextFirstIndex = sortedAlbums.findIndex(a => a.id === next.firstAlbumId);
    
    if (currentLastIndex >= nextFirstIndex) {
      errors.push(`Shelf ${current.shelfNumber} overlaps with shelf ${next.shelfNumber}`);
    }
  }
  
  return { isValid: errors.length === 0, errors };
}

/**
 * Create a new shelf configuration with custom dimensions
 */
export function createShelfConfig(
  rows: number,
  columns: number,
  activeShelves: number,
  heightRatio: number = 1.0,
  widthRatio: number = 1.0
): ShelfConfig {
  return {
    rows,
    columns,
    activeShelves,
    shelfDimensions: {
      heightRatio,
      widthRatio,
    },
    shelfBoundaries: [],
  };
}